import {
  Calculator01Icon,
  Call02Icon,
  Car03Icon,
  CarAlertIcon,
  Exchange01Icon,
  Home01Icon,
  Location01Icon,
  Message01Icon,
  Search01Icon,
  UserCircleIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";

import { cn } from "@car/ui/lib/utils";

import { isConsultLink, openConsult } from "@/lib/consult";
import { SiteHeader, ThemeToggle, salesHref } from "@/components/site-header";
import {
  footerMenu,
  footerSeals,
  linkAttrs,
  mobileBar,
  pageSections,
  telHref,
  useIsCurrent,
  useSiteContent,
  type MenuLink,
  type MobileBarIcon,
  type SitePage,
} from "@/lib/content";
import { toFaDigits } from "@/lib/format";

/** یک پیوند پاورقی؛ پیوند صفحه‌ی جاری aria-current می‌گیرد و روشن‌تر است */
function FooterLink({ link, current }: { link: MenuLink; current: boolean }) {
  return (
    <a
      {...linkAttrs(link)}
      aria-current={current ? "page" : undefined}
      className={cn(
        "inline-flex min-h-6 items-center rounded-sm break-words outline-none max-sm:min-h-11 pointer-coarse:min-h-11 hover:text-brand-ice focus-visible:text-brand-ice focus-visible:ring-2 focus-visible:ring-brand-ice/60",
        current && "font-bold text-brand-ice",
      )}
    >
      {link.label}
    </a>
  );
}

/** ستون‌های پیوند و ردیف پایین از بلوک «منوی پاورقی» در مدیریت محتوا می‌آیند */
export function SiteFooter() {
  const content = useSiteContent();
  const { contact } = content;
  const menu = footerMenu(content.footerMenu);
  const seals = footerSeals(contact);
  const isCurrent = useIsCurrent();
  return (
    <footer className="border-t bg-brand-charcoal text-brand-ice dark:bg-card">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        {/* نشانِ بزرگ: در صفحه‌ی اصلی با اسکرول از خطِ دورگیری به توپُر می‌رسد (data-fill) */}
        <p className="footer-mark" aria-hidden="true">
          <span className="footer-mark-outline">تابان خودرو</span>
          <span className="footer-mark-fill" data-fill>
            تابان خودرو
          </span>
        </p>
      </div>
      <div className="mx-auto max-w-[1400px] px-4 py-12 sm:px-6 sm:py-16">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <div className="flex items-center gap-2">
              <span className="grid size-10 place-items-center rounded-xl bg-brand-ice p-1.5">
                <img src="/logo.webp" alt="" width={320} height={227} className="w-full" />
              </span>
              <span className="text-xl font-black">تابان خودرو</span>
            </div>
            <p className="mt-5 max-w-xs text-sm leading-7 text-brand-ice/65">{contact.tagline}</p>
            <div className="mt-6 grid justify-items-start gap-3">
              <a
                href={salesHref(contact.salesPhone)}
                className="inline-flex items-center gap-2 font-black hover:text-brand-ice/80"
              >
                <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} className="size-5" />
                {contact.salesPhone ? (
                  <>
                    تلفن فروش: <span dir="ltr">{toFaDigits(contact.salesPhone)}</span>
                  </>
                ) : (
                  "تماس با ما و شماره شعب"
                )}
              </a>
              {contact.hotlinePhone ? (
                <a
                  href={telHref(contact.hotlinePhone)}
                  className="inline-flex items-center gap-2 font-black text-brand-yellow"
                >
                  <HugeiconsIcon icon={CarAlertIcon} strokeWidth={1.8} className="size-5" />
                  {contact.hotlineLabel}: <span dir="ltr">{toFaDigits(contact.hotlinePhone)}</span>
                </a>
              ) : null}
            </div>
          </div>
          {menu.columns.map((column, index) => (
            <nav key={index} aria-label={column.title} className="min-w-0">
              <h2 className="font-black">{column.title}</h2>
              <ul className="mt-4 grid gap-3 text-sm text-brand-ice/65">
                {column.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <FooterLink link={link} current={isCurrent(link)} />
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 text-xs text-brand-ice/65 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            {contact.footerNote ? <p>{contact.footerNote}</p> : null}
            {/*
              نمادهای اعتماد فقط وقتی پیوند و تصویرشان گذاشته شده؛ تصویر از سایت صادرکننده می‌آید و
              فقط وقتی ارجاع‌دهنده‌اش دامنه‌ی ثبت‌شده باشد (referrerPolicy=origin روی هر دو). «noreferrer»
              ارجاع‌دهنده را از کلیک حذف می‌کرد و صفحه‌ی نماد دامنه را راستی‌آزمایی نمی‌کرد، پس rel فقط noopener است.
              نماد عمداً ریز است (فقط برای راستی‌آزمایی؛ مشتری از آن چیزی نمی‌خواند)؛ نسبت ۱۲۵×۱۳۶ تصویر در
              جای خود رزرو می‌شود و اگر تصویر نیاید همان‌جا «alt» روی زمینه‌ی سفید می‌ماند.
            */}
            {seals.map((seal, index) => {
              const code = /[?&]Code=([\w-]+)/i.exec(seal.image)?.[1];
              return (
                <a
                  key={index}
                  {...linkAttrs(seal)}
                  rel="noopener"
                  referrerPolicy="origin"
                  className="rounded-md bg-white p-1 text-[10px] leading-tight text-brand-charcoal outline-none transition-shadow hover:ring-2 hover:ring-brand-ice/60 focus-visible:ring-2 focus-visible:ring-brand-ice"
                >
                  <img
                    src={seal.image}
                    alt={seal.label}
                    width={125}
                    height={136}
                    referrerPolicy="origin"
                    loading="lazy"
                    className="h-14 w-auto"
                    {...(code ? { code } : {})}
                  />
                </a>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {menu.bottom.length > 0 ? (
              <nav aria-label="پیوندهای پایین صفحه" className="flex flex-wrap gap-x-5 gap-y-2">
                {menu.bottom.map((link, index) => (
                  <FooterLink key={index} link={link} current={isCurrent(link)} />
                ))}
              </nav>
            ) : null}
            <ThemeToggle
              withLabel
              className="h-9 px-2.5 text-xs text-brand-ice/80 hover:bg-white/10 hover:text-brand-ice focus-visible:ring-brand-ice/60 dark:hover:bg-white/10"
            />
          </div>
        </div>
      </div>
    </footer>
  );
}

const barIcons = {
  car: Car03Icon,
  search: Search01Icon,
  compare: Exchange01Icon,
  consult: Message01Icon,
  panel: UserCircleIcon,
  home: Home01Icon,
  phone: Call02Icon,
  branches: Location01Icon,
  calculator: Calculator01Icon,
} satisfies Record<MobileBarIcon, IconSvgElement>;

/** نوار پایین موبایل از بلوک «نوار پایین موبایل» در مدیریت محتوا می‌آید (تا پنج دکمه؛ ستون‌ها به تعداد دکمه‌ها) */
export function MobileNavigation() {
  const buttons = mobileBar(useSiteContent().mobileBar);
  const isCurrent = useIsCurrent();
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t bg-background px-2 pb-[env(safe-area-inset-bottom)] md:hidden"
      aria-label="دسترسی سریع"
    >
      <div className="grid auto-cols-fr grid-flow-col">
        {buttons.map((button, index) => {
          const current = isCurrent(button);
          const className = cn(
            "grid min-h-16 min-w-0 place-items-center content-center gap-1 px-1 text-xs font-bold text-foreground outline-none hover:text-primary focus-visible:text-primary",
            current && "text-primary",
          );
          const body = (
            <>
              <HugeiconsIcon icon={barIcons[button.icon]} strokeWidth={1.8} className="size-5" />
              <span className="max-w-full truncate">{button.label}</span>
            </>
          );
          // «مشاوره» فرم را همان‌جا باز می‌کند، نه پرش به بخش مشاوره‌ی صفحه‌ی اصلی
          return isConsultLink(button) ? (
            <button
              key={index}
              type="button"
              aria-haspopup="dialog"
              onClick={openConsult}
              className={className}
            >
              {body}
            </button>
          ) : (
            <a
              key={index}
              {...linkAttrs(button)}
              aria-current={current ? "page" : undefined}
              className={className}
            >
              {body}
            </a>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * صفحه‌ی متنی سایت (درباره ما، شرایط استفاده، حریم خصوصی، دسترس‌پذیری) با سربرگ و پاورقی؛
 * همه‌ی متن از بلوک همان صفحه در مدیریت محتوا می‌آید (بند با خط خالی، زیرعنوان با «## »).
 */
export function StaticPage({ page }: { page: SitePage }) {
  const sections = pageSections(page.body);
  return (
    <main className="min-h-[100dvh] bg-background pb-20 text-foreground md:pb-0">
      <SiteHeader />
      <article>
        <header className="border-b bg-muted/45">
          <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-14">
            <nav aria-label="مسیر صفحه" className="text-sm text-muted-foreground">
              <a href="/" className="hover:text-foreground">
                خانه
              </a>
              <span aria-hidden="true" className="mx-2">
                /
              </span>
              <span aria-current="page" className="font-bold text-foreground">
                {page.title}
              </span>
            </nav>
            <h1 className="mt-4 text-3xl font-black text-balance sm:text-4xl">{page.title}</h1>
            {page.lede ? (
              <p className="mt-3 max-w-[60ch] leading-7 text-pretty text-muted-foreground">
                {page.lede}
              </p>
            ) : null}
            {page.updated ? (
              <p className="mt-4 text-xs font-bold text-muted-foreground">{page.updated}</p>
            ) : null}
          </div>
        </header>
        <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-14">
          <div className="max-w-[70ch] leading-8 text-pretty break-words">
            {sections.map((section, index) =>
              section.kind === "heading" ? (
                <h2 key={index} className="mt-10 mb-3 text-xl font-black first:mt-0">
                  {section.text}
                </h2>
              ) : (
                <p key={index} className="mt-4 first:mt-0">
                  {section.text}
                </p>
              ),
            )}
          </div>
        </div>
      </article>
      <SiteFooter />
      <MobileNavigation />
    </main>
  );
}
